import classNames from 'classnames'
import React, { FC } from 'react'
import { InternalExternalLink } from '../../types/InternalExternalLink'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Box } from '../Box'
import { Button } from '../Button'
import { Grid } from '../Grid'
import { Image, ImageProps } from '../Image'
import { LinkList } from '../LinkList'

export interface CategoryTableProps {
  button?: InternalExternalLink
  heading?: string
  imageProps?: ImageProps
  links?: InternalExternalLink[]
  preamble?: string
  subheading?: string
}

export const CategoryTable: FC<CategoryTableProps> = ({
  button,
  heading,
  imageProps,
  links,
  preamble,
  subheading,
}) => (
  <>
    <Grid>
      <div
        className={classNames(
          'col-span-full text-center',
          'tb:col-start-2 tb:col-span-2',
          'sm:col-start-2 sm:col-span-4',
          'lg:col-start-3',
          'xl:col-start-4',
          'xxl:col-start-6'
        )}
      >
        {!isNullOrWhiteSpace(heading) && (
          <h2
            className={classNames(
              'text-primary font-h2 text-h2 mb-30',
              'lg:mb-20',
              'xxl:mb-30'
            )}
          >
            {heading}
          </h2>
        )}

        {!isNullOrWhiteSpace(subheading) && (
          <h3
            className={classNames(
              'text-secondary-dark font-h3 text-h3 mx-60 mb-30',
              'lg:mb-20'
            )}
          >
            {subheading}
          </h3>
        )}

        {!isNullOrWhiteSpace(preamble) && (
          <p
            className={classNames(
              'text-secondary-darker font-t-bold text-t-400 mx-30 mb-45',
              'sm:w-[23.75rem] sm:mx-auto sm:text-t-500',
              'lg:text-t-700 lg:w-[31.875rem]'
            )}
          >
            {preamble}
          </p>
        )}

        {button &&
          !isNullOrWhiteSpace(button.url) &&
          !isNullOrWhiteSpace(button.title) && (
            <Button
              additionalClassName="mb-45 tb:mb-30 lg:mb-45 xl:mb-60"
              isExternalUrl={button.isExternal}
              url={button.url}
            >
              {button?.title}
            </Button>
          )}
      </div>
    </Grid>

    {(imageProps || !!links?.length) && (
      <Grid>
        <div
          className={classNames(
            'relative col-span-full',
            'tb:mt-60',
            'sm:mt-45 sm:col-start-1 sm:col-span-6',
            'lg:col-span-6 lg:col-start-2 lg:mt-0',
            'xl:mt-45 xl:col-start-3',
            'xxl:col-start-4 xxl:col-span-8'
          )}
        >
          {imageProps && (
            <Image
              {...imageProps}
              applyLqip
              className={classNames(
                'm-auto w-full h-[30.625rem] mb-45',
                'tb:h-[29.0625rem]',
                'sm:w-[46.875rem]',
                'md:h-[31.875rem]',
                'lg:w-[52.5rem] lg:h-[35.625rem]',
                'xl:w-[65.625rem] xl:h-[45rem]',
                'xxl:w-[88.125rem] xxl:h-[60rem]'
              )}
              imgClassName="rounded object-cover w-full"
              loading="lazy"
            />
          )}

          {!!links?.length && (
            <div
              className={classNames(
                'col-span-full',
                'tb:absolute tb:right-0 tb:top-0 tb:-mt-60',
                'sm:-mt-75 sm:right-75',
                'md:right-0',
                'lg:-mt-60 lg:-right-30',
                'xl:-mt-120 xl:-right-180',
                'xxl:-mt-150'
              )}
            >
              <Box
                variant="border"
                paddingStyle="custom"
                padding="p-45 xl:pt-30"
                additionalClassName="w-330 min-h-[12.5rem] m-auto tb:m-0 xl:w-330 xxl:w-480 xxl:px-60 xxl:pb-60 xxl:pt-45"
              >
                <LinkList
                  links={links.map(link => ({
                    ...link,
                    url: link.url ?? '',
                    text: link.title,
                  }))}
                />
              </Box>
            </div>
          )}
        </div>
      </Grid>
    )}
  </>
)
